import type { BookMetadata, OutputChapter, OutputVerse } from '../../types';
import booksMetadata from '../text/books.json';
import type { AudioPlayer } from './audio-player';

const textFiles = import.meta.glob('/text/*.json') as {
  [url: string]: () => Promise<OutputChapter>;
};
const timingFiles = import.meta.glob('/audio/*.json') as {
  [url: string]: () => Promise<{
    book: string;
    chapter_number: number;
    verses: number[];
  }>;
};
console.assert(
  Object.keys(timingFiles).length > 0,
  'Should find at least one timing file',
);
/*
 * timingFiles = {
 *   '/audio/AEBWYIN1DA_B01_MAT_001.json': async function json() {
 *     return {
 *       'book': 'MAT",
 *       'chapter_number': 1,
 *       'verses': [6.025, 11.444, ...],
 *     };
 *   },
 *   ...
 * }
 */

export class BibleApp {
  currentView: string;
  currentBook: BookMetadata | null;
  currentChapter: OutputChapter | null;
  breadcrumbs: { view: string; book?: BookMetadata }[];
  audioPlayer: AudioPlayer;

  constructor(audioPlayer: AudioPlayer) {
    this.currentView = 'books'; // books, chapters, reading
    this.currentBook = null;
    this.currentChapter = null;
    this.breadcrumbs = [];
    this.audioPlayer = audioPlayer;
  }

  async loadData() {
    try {
      this.renderBooksView();
    } catch (error) {
      console.error('Failed to load Bible data:', error);
      throw error;
    }
  }

  renderBooksView() {
    this.currentView = 'books';
    this.currentBook = null;
    this.currentChapter = null;
    this.hideAllViews();
    document.getElementById('books-view')!.classList.remove('hidden');
    document.getElementById('breadcrumb')!.classList.add('hidden');

    const booksGrid = document.getElementById('books-grid') as HTMLDivElement;
    booksGrid.innerHTML = '';

    booksMetadata.books.forEach((book) => {
      const bookCard = document.createElement('div');
      bookCard.className = 'book-card';
      bookCard.innerHTML = `
        <div class="book-name">${book.name}</div>
        <div class="book-full-name">${book.fullName}</div>
        <div class="book-stats">عدد الإصحاحات: ${book.numberOfChapters}</div>
      `;

      bookCard.addEventListener('click', () => {
        document.location = `/${book.id}/`;
      });

      booksGrid.appendChild(bookCard);
    });
  }

  showBook(book: BookMetadata) {
    this.breadcrumbs.push({ view: 'books' });
    this.currentBook = book;
    this.currentChapter = null;
    this.currentView = 'chapters';

    this.hideAllViews();
    document.getElementById('chapters-view')!.classList.remove('hidden');
    document.getElementById('breadcrumb')!.classList.remove('hidden');

    document.getElementById('book-title')!.textContent = book.fullName;
    document.getElementById('breadcrumb-text')!.textContent = book.name;

    const chaptersGrid = document.getElementById('chapters-grid')!;
    chaptersGrid.innerHTML = '';

    for (
      let chapterNumber = 1;
      chapterNumber <= book.numberOfChapters;
      chapterNumber++
    ) {
      const chapterCard = document.createElement('div');
      chapterCard.className = 'chapter-card';
      chapterCard.innerHTML = `
        <div class="chapter-number">الإصحاح ${chapterNumber}</div>
      `;

      chapterCard.addEventListener('click', () => {
        document.location = `/${book.id}/${chapterNumber}/`;
      });

      chaptersGrid.appendChild(chapterCard);
    }
  }

  async showChapter(book: BookMetadata, chapterNumber: number) {
    this.breadcrumbs.push({ view: 'chapters', book: book });
    this.currentBook = book;
    const paddedBookNumber = String(book.bookNumber).padStart(2, '0');
    const paddedChapterNumber = String(chapterNumber).padStart(3, '0');
    const url = `/text/${paddedBookNumber}-${book.id}-${paddedChapterNumber}.json`;
    this.currentChapter = await textFiles[url]();
    this.currentView = 'reading';

    this.hideAllViews();
    document.getElementById('reading-view')!.classList.remove('hidden');

    const chapterTitle = `${book.name} - الإصحاح ${chapterNumber}`;
    document.getElementById('chapter-title')!.textContent = chapterTitle;
    document.getElementById('breadcrumb-text')!.textContent = chapterTitle;

    // Update navigation buttons
    const prevBtn = document.querySelector<HTMLButtonElement>('#prev-chapter')!;
    const nextBtn = document.querySelector<HTMLButtonElement>('#next-chapter')!;

    prevBtn.disabled = chapterNumber <= 1;
    nextBtn.disabled = chapterNumber >= this.currentBook.numberOfChapters;

    prevBtn.onclick = () => {
      if (chapterNumber > 1) {
        document.location = `/${book.id}/${chapterNumber - 1}/`;
      }
    };

    nextBtn.onclick = () => {
      if (chapterNumber < book.numberOfChapters) {
        document.location = `/${book.id}/${chapterNumber + 1}/`;
      }
    };

    // Render paragraphs
    this.renderParagraphs(this.currentChapter);

    // Add audio button if audio is available and audio player is set
    this.addAudioButton(this.currentBook, this.currentChapter);

    window.scrollTo(0, 0);
  }

  renderParagraphs(chapter: OutputChapter) {
    const versesContainer = document.getElementById('verses-container')!;
    versesContainer.innerHTML = '';

    chapter.paragraphs.forEach((paragraph) => {
      const paragraphElement = document.createElement('div');
      paragraphElement.className = `paragraph paragraph-${paragraph.type}`;
      paragraphElement.setAttribute('data-marker', paragraph.marker);

      // Handle blank paragraphs
      if (paragraph.type === 'blank') {
        paragraphElement.innerHTML = '<br>';
        versesContainer.appendChild(paragraphElement);
        return;
      }

      // Handle section titles
      if (paragraph.type === 'section') {
        paragraphElement.innerHTML = `<h3 class="section-title">${paragraph.title}</h3>`;
        versesContainer.appendChild(paragraphElement);
        return;
      }

      // Render verses within the paragraph
      paragraph.verses.forEach((verse, index) => {
        const verseElement = document.createElement('span');
        verseElement.className = 'verse-inline';
        verseElement.setAttribute('data-verse', verse.number.toString());

        // Always show verse numbers for all verses
        const verseNumber = document.createElement('span');
        verseNumber.className = 'verse-number-inline';
        verseNumber.textContent = verse.number.toString();
        verseElement.appendChild(verseNumber);

        const verseText = document.createElement('span');
        verseText.className = 'verse-text-inline';
        verseText.textContent = verse.text;
        verseElement.appendChild(verseText);

        // Add click handler for potential audio or other interactions
        verseElement.addEventListener('click', () => {
          this.handleVerseClick(verse).catch(console.error);
        });

        paragraphElement.appendChild(verseElement);

        // Add space between verses in the same paragraph
        if (index < paragraph.verses.length - 1) {
          paragraphElement.appendChild(document.createTextNode(' '));
        }
      });

      versesContainer.appendChild(paragraphElement);
    });
  }

  addAudioButton(book: BookMetadata, chapter: OutputChapter) {
    // Remove any existing audio button
    const existingBtn = document.getElementById('chapter-audio-btn');
    if (existingBtn) {
      existingBtn.remove();
    }

    // Only add audio button if audio player is available
    if (!this.audioPlayer) {
      return;
    }

    const readingControls = document.querySelector('.reading-controls')!;
    const audioBtn = document.createElement('button');
    audioBtn.id = 'chapter-audio-btn';
    audioBtn.className = 'audio-btn';
    audioBtn.innerHTML = '🔊 استمع للإصحاح';
    audioBtn.title = 'تشغيل الصوت';

    audioBtn.addEventListener('click', () => {
      // If audio is currently playing this chapter, toggle play/pause
      if (
        this.audioPlayer.currentChapter &&
        this.audioPlayer.currentChapter.number === chapter.number &&
        this.audioPlayer.currentBook &&
        this.audioPlayer.currentBook.id === book.id
      ) {
        this.audioPlayer.togglePlayPause();
      } else {
        // Otherwise, start playing this chapter
        this.audioPlayer.playChapter(book, chapter);
      }
    });

    // Insert the audio button before the navigation buttons
    readingControls.insertBefore(audioBtn, readingControls.firstChild);
  }

  async handleVerseClick(verse: OutputVerse) {
    // For now, just log the verse click
    console.log('Verse clicked:', verse.number);
    if (this.currentBook === null || this.currentChapter === null) {
      return;
    }

    // Highlight the verse temporarily
    const verseElement = document.querySelector(
      `[data-verse="${verse.number}"]`,
    ) as HTMLDivElement | HTMLSpanElement;
    if (verseElement) {
      verseElement.style.backgroundColor = 'var(--primary-color)';
      verseElement.style.color = 'white';

      setTimeout(() => {
        verseElement.style.backgroundColor = '';
        verseElement.style.color = '';
      }, 1000);
    }

    const paddedAudioNumber = String(this.currentBook.bookNumber - 40).padStart(
      2,
      '0',
    );
    const paddedChapterNumber = String(this.currentChapter.number).padStart(
      3,
      '0',
    );
    const url = `/audio/AEBWYIN1DA_B${paddedAudioNumber}_${this.currentBook.id}_${paddedChapterNumber}.json`;
    // '/audio/' + this.currentChapter.audioFilename.replace('.mp3', '.json');
    console.assert(url.endsWith('.json'));
    if (Object.hasOwn(timingFiles, url)) {
      const timings = await timingFiles[url]();
      if (
        timings.book !== this.currentBook.id ||
        timings.chapter_number !== this.currentChapter.number
      ) {
        throw new Error('Unexpected book and chapter_number data');
      }
      const verseStartSeconds = timings.verses[verse.number - 1];
      if (verseStartSeconds !== undefined) {
        this.audioPlayer.seekToSeconds(verseStartSeconds);
      }
    } else {
      console.warn('Could not find timing file');
    }
  }

  goUp() {
    if (this.breadcrumbs.length === 0) return;

    const previousState = this.breadcrumbs.pop();

    switch (previousState?.view) {
      case 'books':
        document.location = '/';
        break;
      case 'chapters':
        document.location = `/${previousState?.book?.id}/`;
        break;
    }
  }

  hideAllViews() {
    document.getElementById('books-view')!.classList.add('hidden');
    document.getElementById('chapters-view')!.classList.add('hidden');
    document.getElementById('reading-view')!.classList.add('hidden');
  }

  // Search integration
  showSearchResult(bookId: string, chapterNum: number, verseNum: number) {
    const book = booksMetadata.books.find((b) => b.id === bookId);
    if (!book) return;

    // TODO: replace with navigation.navigate(url)
    document.location = `/${book.id}/${chapterNum}/`;

    // Highlight the specific verse
    setTimeout(() => {
      const verseElement = document.querySelector<HTMLElement>(
        `[data-verse="${verseNum}"]`,
      );
      if (verseElement) {
        // Clear any existing highlights
        document.querySelectorAll<HTMLElement>('.verse-inline').forEach((v) => {
          v.style.backgroundColor = '';
          v.style.color = '';
        });

        // Highlight the target verse
        verseElement.style.backgroundColor = 'var(--primary-color)';
        verseElement.style.color = 'white';
        verseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Remove highlight after a few seconds
        setTimeout(() => {
          verseElement.style.backgroundColor = '';
          verseElement.style.color = '';
        }, 3000);
      }
    }, 100);
  }
}
